import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Eyebrow, Headline } from "@/components/Editorial";
import Button from "@/components/Button";
import { Field } from "@/components/Form";
import { Spinner } from "@/components/Loader";
import { affPut } from "@/lib/affiliateApi";
import { useAffiliateAuth } from "@/lib/affiliateAuth";
import { toast } from "@/components/Toast";
import { titleCase, formatPhoneUS } from "@/lib/format";
import { C } from "@/lib/brand";

export default function Profile() {
  const { profile } = useOutletContext();
  const { refreshProfile, logout } = useAffiliateAuth();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    phone: profile?.phone || "",
    socialHandles: {
      instagram: profile?.socialHandles?.instagram || "",
      tiktok: profile?.socialHandles?.tiktok || "",
      youtube: profile?.socialHandles?.youtube || "",
      website: profile?.socialHandles?.website || "",
    },
    notifications: {
      newConversion: profile?.notifications?.newConversion ?? true,
      monthlyReport: profile?.notifications?.monthlyReport ?? true,
      programUpdates: profile?.notifications?.programUpdates ?? true,
    },
  });

  useEffect(() => {
    if (profile) {
      setForm({
        phone: profile.phone || "",
        socialHandles: {
          instagram: profile.socialHandles?.instagram || "",
          tiktok: profile.socialHandles?.tiktok || "",
          youtube: profile.socialHandles?.youtube || "",
          website: profile.socialHandles?.website || "",
        },
        notifications: {
          newConversion: profile.notifications?.newConversion ?? true,
          monthlyReport: profile.notifications?.monthlyReport ?? true,
          programUpdates: profile.notifications?.programUpdates ?? true,
        },
      });
    }
  }, [profile]);

  const setHandle = (k, v) =>
    setForm((p) => ({ ...p, socialHandles: { ...p.socialHandles, [k]: v } }));
  const toggleNotif = (k) =>
    setForm((p) => ({ ...p, notifications: { ...p.notifications, [k]: !p.notifications[k] } }));

  const save = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      await affPut("/affiliate/profile", form, { auth: true });
      await refreshProfile();
      toast("Profile updated.", "success");
      setEditing(false);
    } catch (err) {
      toast(err.detail || "Could not save changes.", "error");
    } finally {
      setSaving(false);
    }
  };

  if (!profile) return null;

  return (
    <div data-testid="affiliate-profile" style={{ maxWidth: 860, margin: "0 auto", padding: "40px 20px 64px" }}>
      <Eyebrow color={C.peachDeep}>★ ACCOUNT</Eyebrow>
      <Headline before="YOUR" italic="settings." size={48} className="mt-4" />

      {/* Account block */}
      <Section title="ACCOUNT" testId="profile-section-account">
        <Row label="Email">{profile.email}</Row>
        <Row label="Affiliate code">
          <span style={{ fontFamily: "ui-monospace, SFMono-Regular, monospace", color: C.ink }}>
            {profile.affiliateCode || "—"}
          </span>
        </Row>
        <Row label="Tier">{profile.tier || "Standard"}</Row>
        <Row label="Joined">{profile.joinedAt ? new Date(profile.joinedAt).toLocaleDateString() : "—"}</Row>
        {!editing ? (
          <Row label="Phone">{formatPhoneUS(profile.phone) || profile.phone || "—"}</Row>
        ) : (
          <div style={{ marginTop: 16 }}>
            <Field
              label="PHONE"
              value={form.phone}
              onChange={(v) => setForm((p) => ({ ...p, phone: v }))}
              placeholder="(555) 123-4567"
              testId="profile-phone-input"
            />
          </div>
        )}
      </Section>

      {/* Payout */}
      <Section title="PAYOUT METHOD" testId="profile-section-payout">
        <Row label="Method">{profile.payoutMethodDisplay || "Not set"}</Row>
        <Row label="Tax form">
          {profile.taxFormType ? `${profile.taxFormType.toUpperCase()} submitted ${profile.taxFormSubmittedAt ? new Date(profile.taxFormSubmittedAt).toLocaleDateString() : ""}` : "Not submitted"}
        </Row>
        <div style={{ marginTop: 14, display: "flex", gap: 10, flexWrap: "wrap" }}>
          <Button variant="ghost" testId="profile-update-payout">UPDATE PAYOUT METHOD</Button>
          <Button variant="ghost" testId="profile-update-tax">UPDATE TAX FORM</Button>
        </div>
      </Section>

      {/* Social */}
      <Section title="SOCIAL HANDLES" testId="profile-section-social">
        {!editing ? (
          <>
            <Row label="Instagram">{profile.socialHandles?.instagram || "—"}</Row>
            <Row label="TikTok">{profile.socialHandles?.tiktok || "—"}</Row>
            <Row label="YouTube">{profile.socialHandles?.youtube || "—"}</Row>
            <Row label="Website">{profile.socialHandles?.website || "—"}</Row>
          </>
        ) : (
          <>
            <Field label="INSTAGRAM" value={form.socialHandles.instagram} onChange={(v) => setHandle("instagram", v)} placeholder="@yourhandle" testId="profile-social-instagram" />
            <Field label="TIKTOK" value={form.socialHandles.tiktok} onChange={(v) => setHandle("tiktok", v)} placeholder="@yourhandle" testId="profile-social-tiktok" />
            <Field label="YOUTUBE" value={form.socialHandles.youtube} onChange={(v) => setHandle("youtube", v)} placeholder="youtube.com/@yourhandle" testId="profile-social-youtube" />
            <Field label="WEBSITE" value={form.socialHandles.website} onChange={(v) => setHandle("website", v)} placeholder="https://yoursite.com" testId="profile-social-website" />
          </>
        )}
      </Section>

      {/* Notifications */}
      <Section title="NOTIFICATIONS" testId="profile-section-notifications">
        <ToggleRow
          testId="notif-new-conversion"
          label="Email me on every new conversion"
          checked={form.notifications.newConversion}
          onToggle={() => toggleNotif("newConversion")}
          disabled={!editing}
        />
        <ToggleRow
          testId="notif-monthly-report"
          label="Monthly performance report"
          checked={form.notifications.monthlyReport}
          onToggle={() => toggleNotif("monthlyReport")}
          disabled={!editing}
        />
        <ToggleRow
          testId="notif-program-updates"
          label="Program updates & new asset drops"
          checked={form.notifications.programUpdates}
          onToggle={() => toggleNotif("programUpdates")}
          disabled={!editing}
        />
      </Section>

      <div style={{ marginTop: 32, display: "flex", gap: 12, flexWrap: "wrap" }}>
        {editing ? (
          <>
            <Button variant="primary" testId="profile-save-btn" onClick={save} disabled={saving}>
              {saving ? <Spinner color={C.cream} /> : "SAVE CHANGES"}
            </Button>
            <Button variant="ghost" testId="profile-cancel-btn" onClick={() => setEditing(false)}>CANCEL</Button>
          </>
        ) : (
          <Button variant="ghost" testId="profile-edit-btn" onClick={() => setEditing(true)}>EDIT PROFILE</Button>
        )}
        <Button variant="ghost" testId="profile-signout-btn" onClick={logout}>SIGN OUT</Button>
      </div>

      <div style={{ marginTop: 56, padding: 22, background: C.bone, fontSize: 14, color: C.warmGray }}>
        <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.2em", color: C.ink, marginBottom: 6 }}>
          ★ NEED HELP?
        </div>
        Email{" "}
        <a href="mailto:affiliates@voya9.com" style={{ color: C.ink, textDecoration: "underline" }}>
          affiliates@voya9.com
        </a>{" "}
        and we'll get back within a business day.
      </div>
    </div>
  );
}

function Section({ title, testId, children }) {
  return (
    <section data-testid={testId} style={{ marginTop: 36, paddingTop: 24, borderTop: `1px solid ${C.warmGray3}` }}>
      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.2em", color: C.warmGray, marginBottom: 14 }}>
        {title}
      </div>
      {children}
    </section>
  );
}

function Row({ label, children }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", fontSize: 14, gap: 14 }}>
      <span style={{ color: C.warmGray, flexShrink: 0 }}>{label}</span>
      <span style={{ color: C.ink, textAlign: "right", minWidth: 0, overflowWrap: "break-word" }}>{children}</span>
    </div>
  );
}

function ToggleRow({ label, checked, onToggle, disabled, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={disabled ? undefined : onToggle}
      style={{
        width: "100%",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "12px 0",
        background: "transparent",
        border: 0,
        borderBottom: `1px solid ${C.warmGray3}`,
        cursor: disabled ? "default" : "pointer",
        textAlign: "left",
        fontSize: 14,
        color: C.ink,
        opacity: disabled ? 0.7 : 1,
      }}
    >
      <span>{label}</span>
      <span
        aria-hidden
        style={{
          width: 36,
          height: 20,
          borderRadius: 999,
          background: checked ? C.ink : C.warmGray3,
          position: "relative",
          flexShrink: 0,
          transition: "background 160ms",
        }}
      >
        <span
          style={{
            position: "absolute",
            top: 2,
            left: checked ? 18 : 2,
            width: 16,
            height: 16,
            borderRadius: "50%",
            background: C.cream,
            transition: "left 160ms",
          }}
        />
      </span>
    </button>
  );
}
